// Libs
import clsx from "clsx";
import React, { useRef, useState } from "react";

// Utils
import {
    type AnchorOrigin,
    Dropdown,
    type DropdownProps,
} from "src/design-system/molecules/modals/Dropdown";

// Components
import { isNil } from "lodash";
import { Button } from "src/design-system/molecules/buttons";
import { ButtonIcon, type ButtonIconProps } from "src/design-system/molecules/buttons/ButtonIcon";
import { IconWrapper, type Icons } from "src/design-system/molecules/icons";
import {
    List,
    ListItem,
    ListItemIcon,
    type ListItemProps,
    ListItemText,
    type ListProps,
} from "src/design-system/molecules/lists";

const DEFAULT_ANCHOR_ORIGIN: AnchorOrigin = { vertical: "bottom", horizontal: "left" };
const DEFAULT_TRANSFORM_ORIGIN: AnchorOrigin = { vertical: "top", horizontal: "left" };

export interface DropdownAction {
    /**
     * Click handler
     */
    action?: () => void | Promise<void>;
    /**
     * Classname of each action item
     */
    className?: string;
    /**
     * If is disabled
     */
    disabled?: boolean;
    /**
     * Icon for action
     */
    icon?: Icons;
    /**
     * If is group title take content and don't use actionable class
     */
    isGroupTitle?: boolean;
    /**
     * Key for react
     */
    key: number | string;
    /**
     * Label for action
     */
    label?: string;
    /**
     * SecondaryLabel for action
     */
    secondaryLabel?: string;
    /**
     * Content for action
     */
    content?: React.ReactNode;
}

export interface ButtonDropdown {
    /**
     * Actions
     */
    actions: DropdownAction[];
    /**
     * Additional className for every action
     */
    actionClassName?: string;
    /**
     * Additional props for every action
     */
    actionProps?: ListItemProps;
    /**
     * Anchor origin
     */
    anchorOrigin?: DropdownProps["anchorOrigin"];
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional className for dropdown
     */
    dropdownClassName?: string;
    /**
     * Additional props for dropdown
     */
    dropdownProps?: Partial<DropdownProps>;
    /**
     * Label of the button
     */
    label?: string;
    /**
     * Additional className for list
     */
    listClassName?: string;
    /**
     * Additional props for list
     */
    listProps?: ListProps;
    /**
     * Additional className for more button
     */
    moreButtonClassName?: string;
    /**
     * Additional props for more button
     */
    moreButtonProps?: ButtonIconProps;
    /**
     * Transform origin
     */
    transformOrigin?: DropdownProps["transformOrigin"];
    /**
     * button Icon
     */
    icon?: Icons;
}

export const ButtonDropdown = React.forwardRef<HTMLDivElement, ButtonDropdown>(
    function ButtonDropdown(
        {
            actions = [],
            actionClassName,
            actionProps,
            anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
            className,
            dropdownClassName,
            dropdownProps,
            label,
            listClassName,
            listProps,
            moreButtonClassName,
            moreButtonProps,
            transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
            icon = "drop",
        },
        ref
    ): JSX.Element {
        const rootClassName = clsx("ds-c-button-dropdown", className);

        const [open, setOpen] = useState(false);
        const anchorRef = useRef<HTMLButtonElement>(null);
        const listItemClassName = clsx("ds-c-button-drop-down--item-list", actionClassName);

        const buttonClassName = clsx(
            "ds-c-button-dropdown",
            {
                "ds-c-button-dropdown--primary":
                    moreButtonProps?.variant === "primary" || isNil(moreButtonProps?.variant),
                "ds-c-button-dropdown--caution": moreButtonProps?.variant === "caution",
            },
            moreButtonClassName
        );

        return (
            <div className={rootClassName} ref={ref}>
                {!isNil(label) && (
                    <Button
                        {...moreButtonProps}
                        className={buttonClassName}
                        onClick={() => setOpen(!open)}
                        ref={anchorRef}
                    >
                        <IconWrapper icon="drop" position="right" size="s">
                            {label}
                        </IconWrapper>
                    </Button>
                )}
                {isNil(label) && (
                    <ButtonIcon
                        {...moreButtonProps}
                        className={moreButtonClassName}
                        onClick={() => setOpen(!open)}
                        ref={anchorRef}
                    >
                        {icon}
                    </ButtonIcon>
                )}

                <Dropdown
                    {...dropdownProps}
                    anchorEl={anchorRef}
                    anchorOrigin={anchorOrigin}
                    className={dropdownClassName}
                    open={open}
                    onClose={() => setOpen(false)}
                    transformOrigin={transformOrigin}
                >
                    <List {...listProps} className={listClassName}>
                        {actions.map((action) => (
                            <ListItem
                                {...actionProps}
                                className={clsx(listItemClassName, action.className)}
                                condensed
                                disabled={action.disabled}
                                key={action.key ?? action.label}
                                onClick={
                                    action.isGroupTitle
                                        ? undefined
                                        : () => {
                                              setOpen(false);
                                              action?.action();
                                          }
                                }
                            >
                                {action.content ?? (
                                    <>
                                        {action.icon && (
                                            <ListItemIcon iconSize="m">{action.icon}</ListItemIcon>
                                        )}
                                        <ListItemText
                                            primary={action.label}
                                            secondary={action.secondaryLabel}
                                        />
                                    </>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Dropdown>
            </div>
        );
    }
);
